import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { ThemeProvider } from "@/shared/providers/ThemeProvider";
import { LanguageProvider } from "@/shared/providers/LanguageProvider";
import { AppPageLayout } from "@/modules/dashboard-shell";
import {
  AccueilPage,
  LoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  AccountTypeSelectPage,
  AdminDashboardPage,
  UsersPage,
  AddUserPage,
  EditUserPage,
  ProvidersPageRouter,
  AddProviderPage,
  EditProviderPage,
  AuthorizationRequestsPage,
  CreateAuthorizationRequestPage,
  EditAuthorizationRequestPage,
  CreateBackupPage,
  RestoreBackupPage,
  SystemLogsPage,
  CacheManagementPage,
  StatisticsPage,
  HospitalExchangeRatesPage,
  PendingInvoicesPage,
  RejectedInvoicesPage,
  SubmitInvoicePage,
  CategoryValidationPage,
} from "@/modules/safecheck";
import { InsurersPage } from "@/modules/safecheck/pages/InsurersPage";
import { AdminReportingPage } from "@/modules/safecheck/pages/AdminReportingPage";
import { CompaniesPageRouter } from "@/modules/safecheck/pages/CompaniesPageRouter";
import { CompanyFormPage } from "@/modules/safecheck/pages/CompanyFormPage";
import { ClaimsControlPage } from "@/modules/safecheck/pages/ClaimsControlPage";
import { ClaimsPortfolioPage } from "@/modules/safecheck/pages/ClaimsPortfolioPage";
import { ClaimsPackagesPage } from "@/modules/safecheck/pages/ClaimsPackagesPage";
import { InsurerCoveragePage } from "@/modules/safecheck/pages/InsurerCoveragePage";
import { ClaimsGridsPage } from "@/modules/safecheck/pages/ClaimsGridsPage";
import { InsurerNetworkPage } from "@/modules/safecheck/pages/InsurerNetworkPage";
import { InsurerReportPage } from "@/modules/safecheck/pages/InsurerReportPage";
import { InsurerAnalysisPage } from "@/modules/safecheck/pages/InsurerAnalysisPage";
import { InsurerFamilyPage } from "@/modules/safecheck/pages/InsurerFamilyPage";
import { DeadlinesPage } from "@/modules/safecheck/pages/DeadlinesPage";
import { ActCodesPage } from "@/modules/safecheck/pages/ActCodesPage";
import { AffectionCodesPage } from "@/modules/safecheck/pages/AffectionCodesPage";
import { ClaimsSettlementsPage } from "@/modules/safecheck/pages/ClaimsSettlementsPage";
import { AuditorDashboardPage } from "@/modules/safecheck/pages/AuditorDashboardPage";
import { BeneficiariesPageRouter } from "@/modules/safecheck/pages/BeneficiariesPageRouter";
import { EmployeesPageRouter } from "@/modules/safecheck/pages/EmployeesPageRouter";
import { InsurerDashboardPage } from "@/modules/safecheck/pages/InsurerDashboardPage";
import { HospitalDashboardPage } from "@/modules/safecheck/pages/HospitalDashboardPage";
import { HospitalActsPageRouter } from "@/modules/safecheck/pages/HospitalActsPageRouter";
import {
  CompanyBillingDetailPageRouter,
  InvoiceDetailPageRouter,
} from "@/modules/safecheck/pages/InvoiceDetailPageRouter";
import { AdminSubmitInvoiceWizard } from "@/modules/safecheck/pages/AdminSubmitInvoiceWizard";
import { InvoicesPageRouter } from "@/modules/safecheck/pages/InvoicesPageRouter";
import { ApprovedInvoicesPageRouter } from "@/modules/safecheck/pages/ApprovedInvoicesPageRouter";
import { UserProfilePageRouter } from "@/modules/safecheck/pages/UserProfilePageRouter";
import { AppStartupGate } from "@/modules/safecheck/components/AppStartupGate";
import { AuthProvider } from "@/modules/safecheck/providers/AuthProvider";
import {
  RedirectIfAuthenticated,
  RequireDashboardAccess,
} from "@/modules/safecheck/components/RequireDashboardAccess";

function DashboardLayout() {
  return (
    <AppPageLayout>
      <Outlet />
    </AppPageLayout>
  );
}

function AuditorDashboardRoute() {
  return <AuditorDashboardPage />;
}

function EditInvoiceRoute() {
  const { id } = useParams();
  const invoiceId = Number.parseInt(id ?? "", 10);
  return (
    <AdminSubmitInvoiceWizard
      invoiceId={Number.isNaN(invoiceId) || invoiceId <= 0 ? undefined : invoiceId}
    />
  );
}

function InvoiceDetailRoute() {
  const { id } = useParams();
  const displayId = Number.parseInt(id ?? "", 10);
  return <InvoiceDetailPageRouter displayId={Number.isNaN(displayId) ? 0 : displayId} />;
}

/** Fiche d'une facture pour le compte Assurance (« Voir » des listes de factures). */
function BillingDetailRoute() {
  const { id } = useParams();
  const displayId = Number.parseInt(id ?? "", 10);
  return <CompanyBillingDetailPageRouter displayId={Number.isNaN(displayId) ? 0 : displayId} />;
}

export default function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
        <AppStartupGate>
        <Routes>
          <Route element={<RedirectIfAuthenticated />}>
            <Route path="/" element={<AccueilPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/forgot" element={<ForgotPasswordPage />} />
            <Route path="/login/reset" element={<ResetPasswordPage />} />
          </Route>
          <Route path="/select-account" element={<AccountTypeSelectPage />} />

          <Route path="/dashboard" element={<RequireDashboardAccess />}>
            <Route element={<DashboardLayout />}>
            <Route index element={<Navigate to="admin" replace />} />
            <Route path="admin" element={<AdminDashboardPage />} />
            <Route path="auditor" element={<AuditorDashboardRoute />} />
            <Route path="hospital" element={<HospitalDashboardPage />} />
            <Route path="enterprise" element={<InsurerDashboardPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/create" element={<AddUserPage />} />
            <Route path="users/:id/edit" element={<EditUserPage />} />
            <Route path="providers" element={<ProvidersPageRouter />} />
            <Route path="providers/create" element={<AddProviderPage />} />
            <Route path="providers/:id/edit" element={<EditProviderPage />} />
            <Route path="providers/:id" element={<ProvidersPageRouter />} />
            <Route path="companies" element={<CompaniesPageRouter />} />
            <Route path="companies/create" element={<CompanyFormPage />} />
            <Route path="companies/:id/edit" element={<CompanyFormPage />} />
            <Route path="companies/:id" element={<CompaniesPageRouter />} />
            <Route path="employees" element={<EmployeesPageRouter />} />
            <Route path="employees/:id" element={<EmployeesPageRouter />} />
            <Route path="beneficiaries" element={<BeneficiariesPageRouter />} />
            <Route path="beneficiaries/family/:id" element={<InsurerFamilyPage />} />
            <Route path="beneficiaries/:type/:id" element={<BeneficiariesPageRouter />} />
            <Route path="insurers" element={<InsurersPage />} />
            <Route path="claims/control" element={<ClaimsControlPage />} />
            <Route path="claims/portfolio" element={<ClaimsPortfolioPage />} />
            <Route path="claims/packages" element={<ClaimsPackagesPage />} />
            <Route path="coverage" element={<InsurerCoveragePage />} />
            <Route path="claims/grids" element={<ClaimsGridsPage />} />
            <Route path="network" element={<InsurerNetworkPage />} />
            <Route path="report" element={<InsurerReportPage />} />
            <Route path="report/details/:kind" element={<InsurerAnalysisPage />} />
            <Route path="reporting" element={<AdminReportingPage />} />
            <Route path="deadlines" element={<DeadlinesPage />} />
            <Route path="claims/settlements" element={<ClaimsSettlementsPage />} />
            <Route path="acts/by-hospital" element={<HospitalActsPageRouter />} />
            <Route path="acts/category-validation" element={<CategoryValidationPage />} />
            <Route path="referentials/acts" element={<ActCodesPage />} />
            <Route path="referentials/affections" element={<AffectionCodesPage />} />
            <Route path="acts/standard-codes/*" element={<Navigate to="/dashboard/referentials/acts" replace />} />
            <Route path="invoices" element={<InvoicesPageRouter />} />
            <Route path="invoices/approved" element={<ApprovedInvoicesPageRouter />} />
            <Route path="exchange-rates" element={<HospitalExchangeRatesPage />} />
            <Route path="invoices/pending" element={<PendingInvoicesPage />} />
            <Route path="invoices/rejected" element={<RejectedInvoicesPage />} />
            <Route path="invoices/submit" element={<SubmitInvoicePage />} />
            <Route path="invoices/:id/edit" element={<EditInvoiceRoute />} />
            <Route path="invoices/:id" element={<InvoiceDetailRoute />} />
            <Route path="billing/:id" element={<BillingDetailRoute />} />
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="requests" element={<AuthorizationRequestsPage />} />
            <Route path="requests/create" element={<CreateAuthorizationRequestPage />} />
            <Route path="requests/:id/edit" element={<EditAuthorizationRequestPage />} />
            <Route path="backup" element={<Navigate to="/dashboard/backup/create" replace />} />
            <Route path="backup/create" element={<CreateBackupPage />} />
            <Route path="backup/restore" element={<RestoreBackupPage />} />
            <Route path="system-logs" element={<SystemLogsPage />} />
            <Route path="cache" element={<CacheManagementPage />} />
            <Route path="settings" element={<Navigate to="/dashboard/settings/user" replace />} />
            <Route path="settings/user" element={<UserProfilePageRouter />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </AppStartupGate>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
